import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { isNative } from './platformService';

/**
 * Haptics Service - Native haptic feedback wrapper
 *
 * Provides tactile feedback on native platforms (iOS/Android).
 * Silently degrades on web platforms.
 *
 * Usage:
 *   import { hapticImpact, hapticSuccess, hapticError } from '../services/hapticsService';
 *
 *   // Light tap feedback for button presses
 *   await hapticImpact();
 *
 *   // Success feedback (round created, RSVP sent, etc.)
 *   await hapticSuccess();
 *
 *   // Error feedback (validation errors, API failures)
 *   await hapticError();
 */

/**
 * Trigger an impact haptic feedback
 * Use for button taps, toggles, and interactive elements
 *
 * @param {'light' | 'medium' | 'heavy'} style - Impact intensity
 */
export const hapticImpact = async (style = 'light') => {
  if (!isNative()) return;

  try {
    const impactStyle = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    }[style] || ImpactStyle.Light;

    await Haptics.impact({ style: impactStyle });
  } catch {
    // Silently fail - haptics are enhancement only
  }
};

/**
 * Trigger a notification-style haptic
 * Use for success, warning, or error states
 *
 * @param {'success' | 'warning' | 'error'} type - Notification type
 */
export const hapticNotification = async (type = 'success') => {
  if (!isNative()) return;

  try {
    const notificationType = {
      success: NotificationType.Success,
      warning: NotificationType.Warning,
      error: NotificationType.Error,
    }[type] || NotificationType.Success;

    await Haptics.notification({ type: notificationType });
  } catch {
    // Silently fail - haptics are enhancement only
  }
};

/**
 * Success haptic - use after successful operations
 * (round created, calendar connected, RSVP sent, etc.)
 */
export const hapticSuccess = () => hapticNotification('success');

/**
 * Error haptic - use after errors
 * (validation failures, API errors, etc.)
 */
export const hapticError = () => hapticNotification('error');

/**
 * Warning haptic - use for warnings or confirmations
 */
export const hapticWarning = () => hapticNotification('warning');

/**
 * Light tap haptic - use for button presses
 */
export const hapticTap = () => hapticImpact('light');

/**
 * Selection changed haptic - use for toggles, switches, selections
 */
export const hapticSelection = async () => {
  if (!isNative()) return;

  try {
    await Haptics.selectionChanged();
  } catch {
    // Silently fail
  }
};

export default {
  hapticImpact,
  hapticNotification,
  hapticSuccess,
  hapticError,
  hapticWarning,
  hapticTap,
  hapticSelection,
};
